import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "./ListBoxes.css";

const Listboxes = ({ customers = [], sites = [], assignedSitesProp = [], onAssignedSitesChange }) => {
	const [customerSelected, setCustomerSelected] = useState(null);
	const [customerSearchTerm, setCustomerSearchTerm] = useState("");
	const [sitesSearchTerm, setSitesSearchTerm] = useState("");
	const [siteSelected, setSiteSelected] = useState([]);
	const [filteredSites, setFilteredSites] = useState([]);
	const [selectedSites, setSelectedSites] = useState({});
	const [assignedSites, setAssignedSites] = useState(assignedSitesProp);
	const [selectedSitesToMove, setSelectedSitesToMove] = useState([]);

	useEffect(() => {
		if (assignedSitesProp && assignedSitesProp.length > 0) {
			const initialSelectedSites = {};
			assignedSitesProp.forEach((assigned) => {
				const customer = customers.find((c) => c.cus_id === assigned.customer);
				if (customer) {
					initialSelectedSites[customer.cus_id] = {
						customer,
						sites: sites.filter((site) => assigned.sites.includes(site.site_id)),
					};
				}
			});

			// Set the selected sites for pre-filling
			setSelectedSites(initialSelectedSites);
			setAssignedSites(assignedSitesProp);

			// Filter out the assigned sites from the available sites
			const assignedSiteIds = assignedSitesProp.flatMap((assigned) => assigned.sites);
			const remainingSites = sites.filter((site) => !assignedSiteIds.includes(site.site_id));
			setFilteredSites(remainingSites);
		} else {
			// Set the selected sites for pre-filling
			setSelectedSites([]);
			setAssignedSites([]);
		}
	}, [assignedSitesProp, customers, sites]);

	useEffect(() => {
		if (onAssignedSitesChange) {
			// Call the parent callback whenever assignedSites changes
			onAssignedSitesChange(assignedSites);
		}
	}, [assignedSites, onAssignedSitesChange]);

	// Track previous assignedSitesProp to compare for changes

	const handleCustomerListItemClick = (item) => {
		if (!item) return;
		setCustomerSelected(item);
		const customerSites = sites.filter((site) => site && site.cus_id === item.cus_id);
		const remainingCustomerSites = customerSites.filter(
			(site) => !selectedSites[item.cus_id]?.sites.some((s) => s.site_id === site.site_id)
		);
		setFilteredSites(remainingCustomerSites);
		setSiteSelected([]);
	};

	const handleSiteListItemClick = (item) => {
		if (!item) return;
		setSiteSelected((prevSiteSelected) =>
			prevSiteSelected.includes(item)
				? prevSiteSelected.filter((selected) => selected !== item)
				: [...prevSiteSelected, item]
		);
	};

	const handleSelectedSiteClick = (site) => {
		setSelectedSitesToMove((prevSelectedSites) => {
			if (prevSelectedSites.includes(site)) {
				return prevSelectedSites.filter((s) => s !== site);
			} else {
				return [...prevSelectedSites, site];
			}
		});
	};

	const handleCustomerSearchChange = (value) => {
		setCustomerSearchTerm(value);
	};

	const handleSitesSearchChange = (value) => {
		setSitesSearchTerm(value);
	};

	const filteredCustomers = customers.filter(
		(item) =>
			item &&
			(item.customer_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
				item.customer_id?.toLowerCase().includes(customerSearchTerm.toLowerCase()))
	);

	const filteredSitesSearch = filteredSites.filter(
		(item) =>
			item &&
			(item.customer_site_name?.toLowerCase().includes(sitesSearchTerm.toLowerCase()) ||
				item.customer_site_number?.toLowerCase().includes(sitesSearchTerm.toLowerCase()))
	);

	const handleMoveToSelectedSites = () => {
		if (!customerSelected || siteSelected.length === 0) return;

		setSelectedSites((prevSelectedSites) => {
			const newSelectedSites = { ...prevSelectedSites };
			if (!newSelectedSites[customerSelected.cus_id]) {
				newSelectedSites[customerSelected.cus_id] = {
					customer: customerSelected,
					sites: [],
				};
			}
			newSelectedSites[customerSelected.cus_id].sites = [
				...new Set([...newSelectedSites[customerSelected.cus_id].sites, ...siteSelected]),
			];

			return newSelectedSites;
		});

		setAssignedSites((prevAssignedSites) => {
			const updatedAssignedSites = [...prevAssignedSites];
			const customerAssigned = updatedAssignedSites.find(
				(assigned) => assigned.customer === customerSelected.cus_id
			);

			if (customerAssigned) {
				customerAssigned.sites = [
					...new Set([...customerAssigned.sites, ...siteSelected.map((site) => site.site_id)]),
				];
			} else {
				updatedAssignedSites.push({
					customer: customerSelected.cus_id,
					sites: siteSelected.map((site) => site.site_id),
				});
			}
			return updatedAssignedSites;
		});

		setFilteredSites((prevFilteredSites) => prevFilteredSites.filter((site) => !siteSelected.includes(site)));
		setSiteSelected([]);
		setCustomerSelected(null);
	};

	const handleMoveToFilteredSites = () => {
		if (selectedSitesToMove.length === 0) return;

		setFilteredSites((prevFilteredSites) => [...prevFilteredSites, ...selectedSitesToMove]);

		if (customerSelected) {
			setSelectedSites((prevSelectedSites) => {
				const newSelectedSites = { ...prevSelectedSites };
				newSelectedSites[customerSelected.cus_id].sites = newSelectedSites[
					customerSelected.cus_id
				].sites.filter((site) => !selectedSitesToMove.includes(site));

				if (newSelectedSites[customerSelected.cus_id].sites.length === 0) {
					delete newSelectedSites[customerSelected.cus_id];
				}

				return newSelectedSites;
			});

			setAssignedSites((prevAssignedSites) =>
				prevAssignedSites
					.map((assigned) =>
						assigned.customer === customerSelected?.cus_id
							? {
									...assigned,
									sites: assigned.sites.filter(
										(id) => !selectedSitesToMove.map((s) => s.site_id).includes(id)
									),
							  }
							: assigned
					)
					.filter((assigned) => assigned.sites.length > 0)
			);
		} else {
			setAssignedSites(
				(prevAssignedSites) =>
					prevAssignedSites
						.map((assigned) => ({
							...assigned,
							sites: assigned.sites.filter(
								(id) => !selectedSitesToMove.some((selectedSite) => selectedSite.site_id === id)
							),
						}))
						.filter((assigned) => assigned.sites.length > 0) // Remove customers with no sites left
			);
			setSelectedSites((prevSelectedSites) => {
				const newSelectedSites = { ...prevSelectedSites };
				Object.keys(newSelectedSites).forEach((cusId) => {
					newSelectedSites[cusId].sites = newSelectedSites[cusId].sites.filter(
						(site) => !selectedSitesToMove.some((s) => s.site_id === site.site_id)
					);

					if (newSelectedSites[cusId].sites.length === 0) {
						delete newSelectedSites[cusId];
					}
				});
				return newSelectedSites;
			});
		}

		setSelectedSitesToMove([]);
	};

	return (
		<>
			<div className="user-cus-site-selection">
				{/* Customer List Box */}
				<div className="list-box">
					<Box pb={2}>
						<ContextFilterAndSort
							value={customerSearchTerm}
							onSearchChange={handleCustomerSearchChange}
							placeholder="Search Customers"
							showSearch={true}
							showSort={false}
						/>
					</Box>
					<div className="list-box-container">
						{filteredCustomers.map((item, index) => (
							<div
								key={index}
								className={`list-box-item ${customerSelected === item ? "selected" : ""}`}
								onClick={() => handleCustomerListItemClick(item)}>
								<p className="title">{item.customer_name}</p>
								<p className="description">{item.customer_id}</p>
							</div>
						))}
					</div>
				</div>

				{/* Sites List Box */}
				<div className="list-box">
					<Box pb={2}>
						<ContextFilterAndSort
							value={sitesSearchTerm}
							onSearchChange={handleSitesSearchChange}
							placeholder="Search Customer Locations"
							showSearch={true}
							showSort={false}
						/>
					</Box>
					<div className="list-box-container">
						<div className="list-box-item-header">
							<p className="title">
								{customerSelected
									? `${customerSelected.cus_id} - ${customerSelected.customer_name}`
									: "Please select a customer"}
							</p>
						</div>
						{customerSelected &&
							filteredSitesSearch.map((item, index) => (
								<div
									key={index}
									className={`list-box-item ${siteSelected.includes(item) ? "selected" : ""}`}
									onClick={() => handleSiteListItemClick(item)}>
									<p className="title">{item.customer_site_name}</p>
									<p className="description">{item.customer_site_number}</p>
								</div>
							))}
					</div>
				</div>

				{/* Chevron Buttons */}
				<div className="list-controls">
					<Button variant="outlined" color="primary" onClick={handleMoveToSelectedSites}>
						<ChevronRight />
					</Button>
					<Button variant="outlined" color="primary" onClick={handleMoveToFilteredSites}>
						<ChevronLeft />
					</Button>
				</div>

				{/* Selected Sites List Box */}
				<div className="list-box">
					<div className="list-box-container" style={{ minHeight: "490px" }}>
						{Object.keys(selectedSites).map((cus_id) => (
							<React.Fragment key={cus_id}>
								<div className="list-box-item-header">
									<p className="title">
										{selectedSites[cus_id].customer.cus_id} -{" "}
										{selectedSites[cus_id].customer.customer_name}
									</p>
								</div>
								{selectedSites[cus_id].sites.map((site) => (
									<div
										key={site.site_id}
										className={`list-box-item ${
											selectedSitesToMove.includes(site) ? "selected" : ""
										}`}
										onClick={() => handleSelectedSiteClick(site)}>
										<p className="title">{site.customer_site_name}</p>
										<p className="description">{site.customer_site_number}</p>
									</div>
								))}
							</React.Fragment>
						))}
					</div>
				</div>
			</div>
		</>
	);
};

export default Listboxes;
